/**
 * Refunds, and the corrections that fix them.
 *
 * **A refund is never one lump.** The component lines are what the ledger reverses and what a
 * devotee is owed an explanation of, so they are shown as lines — including the ones worth
 * nothing, because "why did I only get the temple charge back" is answered by the zeros.
 *
 * **A refund's status comes from the gateway, not from us.** `initiated` means we asked;
 * `pending` means Razorpay has it; `processed` means the money reached the devotee and a webhook
 * said so. The screen uses those words rather than a friendlier synonym, because support quotes
 * this screen down a phone line.
 *
 * **Corrections are maker-checker, and the screen cannot pretend otherwise.** Approve is offered
 * on somebody else's adjustment; on your own the server returns `403 self_approval` and the
 * refusal is shown verbatim. One person with two clicks is one person.
 *
 * **Raising a refund by hand is finance's, and it is typed, not clicked.** The booking is named
 * by the `BMP…` code a ticket quotes, the amount is entered per component so the ledger can
 * reverse it line by line, and the memo is the confirmation's reason — a refund nobody can
 * explain is one nobody can defend at month end.
 */
import { useState } from 'react';
import { Button, StatusChip } from '../components/primitives';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { Input, Select } from '../components/forms';
import { ConfirmAction, FilterBar, Money, ScopeAction, Section, StatTile } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { formatInr } from '../lib/money';
import {
  useAdjustments,
  useApproveAdjustment,
  useRaiseRefund,
  useRetryRefund,
  useRefunds,
  useRejectAdjustment,
  type AdminRefundRow,
  type ManualRefundRequest,
  type RefundRow,
} from '../lib/api/hooks/refunds';
import { AdjustmentDraft } from './AdjustmentDraft';
import { RefundMatrixButton } from './RefundMatrix';
import { useUrlFilters, useUrlSearch, useUrlTab, type FilterDef } from '../lib/filterCodecs';
import { formatDateTime } from '../lib/datetime';

const PAGE_SIZE = 25;

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'initiated', label: 'Not sent yet' },
  { key: 'pending', label: 'With the gateway' },
  { key: 'processed', label: 'Paid' },
  { key: 'failed', label: 'Failed' },
];

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same queue. */
const TAB_KEYS = TABS.map((t) => t.key);

/**
 * What a refund's status means in words support can say out loud.
 *
 * `initiated` and `pending` are genuinely different — one is money we have not asked for yet,
 * the other is money the gateway is moving — and collapsing them into "processing" would make
 * "where is my money" unanswerable.
 */
export function refundState(row: RefundRow): {
  label: string;
  tone: 'success' | 'error' | 'warn' | 'neutral';
} {
  if (row.status === 'processed') return { label: 'paid', tone: 'success' };
  if (row.status === 'failed') return { label: 'failed at the gateway', tone: 'error' };
  if (row.status === 'pending') return { label: 'with the gateway', tone: 'warn' };
  return { label: 'not sent yet', tone: 'neutral' };
}

/** A policy refund can be explained by a window; a manual one only by the person who decided. */
export function refundOrigin(row: RefundRow): string {
  return row.initiated_by === 'ops' ? 'decided by ops' : `${row.initiated_by} · by policy`;
}

/** Why the money went back, in the words an operator would use; unknown codes fall back to the code. */
const CAUSES: Readonly<Record<string, string>> = {
  customer_cancelled: 'devotee cancelled',
  pandit_cancelled: 'pandit cancelled',
  provider_no_show: 'pandit no-show',
  no_provider_available: 'no pandit available',
  acceptance_expired: 'not accepted in time',
  dispute_resolution: 'dispute upheld',
  duplicate_payment: 'duplicate capture',
  pricing_error: 'pricing error',
};

const KINDS: Readonly<Record<AdminRefundRow['basis']['kind'], string>> = {
  policy: 'Policy',
  goodwill: 'Goodwill',
  reversal: 'Reversal',
  full: 'Full',
  partial: 'Partial',
};

/**
 * The design's "Basis" cell (admin doc 17 §3): the kind the server decided, then the cause.
 *
 * A policy refund names its window, the one the cancellation fell in ("cancellation 4–24 h",
 * or "cancellation ≥ 72 h" for the open-ended first window). Everything else names its reason.
 * Goodwill is its own reason, so it stands alone.
 */
export function refundBasis(row: AdminRefundRow): string {
  const cause = basisCause(row);
  // The matrix row leads, so "How refunds resolve" can be read against it (doc 17 §3).
  return row.basis.matrix_row ? `${row.basis.matrix_row} · ${cause}` : cause;
}

function basisCause(row: AdminRefundRow): string {
  const { kind, window_hours_min: low, window_hours_max: high } = row.basis;
  if (kind === 'goodwill') return KINDS.goodwill;
  if (kind === 'policy') {
    if (low === null || low === undefined) return `${KINDS.policy} · cancellation`;
    const window = high === null || high === undefined ? `≥ ${low} h` : `${low}–${high} h`;
    return `${KINDS.policy} · cancellation ${window}`;
  }
  return `${KINDS[kind]} · ${CAUSES[row.reason_code] ?? row.reason_code}`;
}

/** The closed set the server accepts — reporting asks "why", and prose cannot answer it. */
const REASON_CODES = [
  'goodwill',
  'dispute_resolution',
  'pricing_error',
  'duplicate_payment',
  'provider_no_show',
  'pandit_cancelled',
  'customer_cancelled',
  'no_provider_available',
  'acceptance_expired',
] as const;

/**
 * The browser's filters (doc 17 §4 item 1). A reason the console cannot raise by hand is still
 * one a refund can carry, so the filter offers every code the server stores.
 */
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const REFUND_FILTERS = {
  reason: { kind: 'select', options: [...REASON_CODES, 'late_capture_slot_lost'] },
  raised: { kind: 'select', options: ['devotee', 'ops', 'system', 'dispute'] },
  days: { kind: 'daterange' },
} as const satisfies Record<string, FilterDef>;

/** The refund-line words the published cancellation policy uses, not the checkout's. */
const COMPONENTS = ['service', 'samagri', 'travel', 'platform_fee', 'temple_charge'] as const;

/**
 * Rupees typed per component, as the paise lines the server takes. A blank, zero or unreadable
 * amount is no line at all rather than a zero line the server would refuse.
 */
export function refundLines(rupees: Readonly<Record<string, string>>): ManualRefundRequest['lines'] {
  return COMPONENTS.flatMap((component) => {
    const paise = Math.round(Number(rupees[component] ?? '') * 100);
    return Number.isFinite(paise) && paise > 0 ? [{ component, amount_paise: paise }] : [];
  });
}

const LINE_WORDS: Readonly<Record<string, string>> = {
  service: 'Service',
  samagri: 'Samagri',
  travel: 'Travel',
  platform_fee: 'Platform fee',
  temple_charge: 'Temple charge',
};

function RefundDetail({ row, onClose }: { row: AdminRefundRow; onClose: () => void }) {
  const state = refundState(row);
  return (
    <DetailPage
      title={refundBasis(row)}
      code={row.public_code}
      status={<StatusChip tone={state.tone} label={state.label} />}
      onClose={onClose}
      audit="Read-only. A refund is corrected by a new compensating entry, never by editing this one."
    >
      <KV
        rows={[
          ['Booking', `#${row.booking_id}`],
          ['Raised', refundOrigin(row)],
          ['Reason code', row.reason_code],
          ['Amount', <Money key="amount" paise={row.amount_paise} />],
          ['Requested', formatDateTime(row.created_at)],
          ['Processed', row.processed_at ? formatDateTime(row.processed_at) : '—'],
          ['ARN', row.arn ?? '—'],
          ...(row.failure_reason ? [['Gateway said', row.failure_reason] as const] : []),
        ]}
      />
      <DataTable
        density="compact"
        columns={[
          { key: 'component', label: 'Line' },
          { key: 'base', label: 'Paid', align: 'right' },
          { key: 'fraction', label: 'Refundable', align: 'right' },
          { key: 'amount', label: 'Refunded', align: 'right' },
        ]}
        rows={row.lines.map((line) => ({
          id: line.component,
          component: LINE_WORDS[line.component] ?? line.component,
          base: <Money paise={line.base_amount_paise} />,
          fraction: `${Math.round(Number(line.refundable_fraction) * 100)}%`,
          amount: <Money paise={line.amount_paise} />,
        }))}
      />
    </DetailPage>
  );
}

export function RefundsQueue() {
  const { scope, toast } = useConsole();
  const [tab, setTab] = useUrlTab('refunds', TAB_KEYS, 'all');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useUrlFilters('refunds', REFUND_FILTERS);
  const [q, setQ] = useUrlSearch('refunds');
  // A range is only a filter once both ends are days and in order; until then the boxes hold
  // what was typed and the URL keeps the last whole range.
  const [from, setFrom] = useState(filters.days?.from ?? '');
  const [to, setTo] = useState(filters.days?.to ?? '');
  const [code, setCode] = useState('');
  const [reasonCode, setReasonCode] = useState<string>('goodwill');
  const [rupees, setRupees] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [retrying, setRetrying] = useState<AdminRefundRow | null>(null);
  const [open, setOpen] = useState<AdminRefundRow | null>(null);
  const raise = useRaiseRefund();
  const retry = useRetryRefund();
  const lines = refundLines(rupees);
  const totalPaise = lines.reduce((sum, line) => sum + line.amount_paise, 0);

  const refunds = useRefunds({
    status: tab === 'all' ? undefined : tab,
    reason: filters.reason,
    initiated_by: filters.raised,
    date_from: filters.days?.from,
    date_to: filters.days?.to,
    q,
    page,
    page_size: PAGE_SIZE,
  });
  const pending = useAdjustments('pending_approval');
  const approve = useApproveAdjustment();
  const reject = useRejectAdjustment();

  const rows = refunds.data?.items ?? [];
  const total = refunds.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const narrow = (apply: () => void) => {
    apply();
    setPage(1);
  };

  const pickDays = (nextFrom: string, nextTo: string) => {
    setFrom(nextFrom);
    setTo(nextTo);
    if (nextFrom === '' && nextTo === '') narrow(() => setFilters({ days: undefined }));
    else if (DAY.test(nextFrom) && DAY.test(nextTo) && nextFrom <= nextTo)
      narrow(() => setFilters({ days: { from: nextFrom, to: nextTo } }));
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <RefundMatrixButton />
      </div>
      <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
        A refund's status is the gateway's word, not ours. "Not sent yet" means we have not asked;
        "with the gateway" means Razorpay has it; "paid" means a webhook confirmed the money
        reached the devotee.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: 'var(--sp-md)',
        }}
      >
        <StatTile label="Refunds" value={refunds.isSuccess ? total : '—'} note="in this view" />
        <StatTile
          label="Failed"
          value={refunds.isSuccess ? rows.filter((r) => r.status === 'failed').length : '—'}
          note="on this page · still owed to the devotee"
          tone={rows.some((r) => r.status === 'failed') ? 'error' : 'default'}
        />
        <StatTile
          label="Awaiting a second pair of eyes"
          value={pending.isSuccess ? pending.data.total : '—'}
          note="adjustments"
        />
      </div>

      <FilterBar
        q={q}
        onQ={(value) => narrow(() => setQ(value))}
        placeholder="Refund, booking, gateway id or ARN"
        tabs={TABS}
        tab={tab}
        onTab={(next) => narrow(() => setTab(next))}
        extra={
          <>
            <Select
              ariaLabel="Filter by reason"
              placeholder="Any reason"
              value={filters.reason ?? ''}
              options={REFUND_FILTERS.reason.options}
              optionLabel={(code) => code.replace(/_/g, ' ')}
              onChange={(value) => narrow(() => setFilters({ reason: value || undefined }))}
            />
            <Select
              ariaLabel="Filter by who raised it"
              placeholder="Raised by anyone"
              value={filters.raised ?? ''}
              options={REFUND_FILTERS.raised.options}
              optionLabel={(code) => code.replace(/_/g, ' ')}
              onChange={(value) => narrow(() => setFilters({ raised: value || undefined }))}
            />
            <Input
              label="From (IST)"
              placeholder="YYYY-MM-DD"
              value={from}
              error={from === '' || DAY.test(from) ? undefined : 'YYYY-MM-DD'}
              onChange={(value) => pickDays(value.trim(), to)}
              style={{ width: 130 }}
            />
            <Input
              label="To (IST)"
              placeholder="YYYY-MM-DD"
              value={to}
              error={to === '' || DAY.test(to) ? undefined : 'YYYY-MM-DD'}
              onChange={(value) => pickDays(from, value.trim())}
              style={{ width: 130 }}
            />
          </>
        }
      />

      {refunds.isPending && <LoadingState message="Reading refunds…" />}
      {refunds.isError && (
        <ErrorState title="Could not read refunds" cause={errorMessage(refunds.error)} />
      )}
      {refunds.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="undo-2"
          title={tab === 'failed' ? 'No failed refunds' : 'Nothing here'}
          body={
            tab === 'failed'
              ? 'Every refund the gateway took has gone out.'
              : 'No refund matches this filter.'
          }
          inline
        />
      )}

      {rows.length > 0 && (
        <Section
          title="Refunds"
          note={`${total} ${total === 1 ? 'refund' : 'refunds'} · page ${page} of ${pages}`}
          pad={false}
        >
          <DataTable
            density="compact"
            onRowClick={(clicked) => setOpen(rows.find((row) => row.public_code === clicked.id)!)}
            columns={[
              { key: 'code', label: 'Refund', mono: true },
              { key: 'basis', label: 'Basis' },
              { key: 'origin', label: 'Raised' },
              { key: 'amount', label: 'Amount', align: 'right' },
              { key: 'lines', label: 'Lines', align: 'right' },
              { key: 'state', label: 'Status' },
              { key: 'arn', label: 'ARN', mono: true },
              { key: 'act', label: '' },
            ]}
            rows={rows.map((row) => {
              const state = refundState(row);
              return {
                id: row.public_code,
                code: row.public_code,
                basis: refundBasis(row),
                origin: refundOrigin(row),
                amount: <Money paise={row.amount_paise} />,
                lines: row.lines.length,
                state: <StatusChip tone={state.tone} label={state.label} />,
                // The number a bank asks for when somebody says the money has not arrived.
                arn: row.arn ?? '—',
                // Built from §2.1 by the owner's 2026-10-26 ruling: the design's drawer has no Retry.
                act:
                  row.status === 'failed' ? (
                    // Its own click, not the row's: Retry asks to confirm, it does not open the detail.
                    <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                      <ScopeAction scope={scope} need="finance" size="sm" variant="secondary" onClick={() => setRetrying(row)}>
                        Retry
                      </ScopeAction>
                    </span>
                  ) : null,
              };
            })}
          />
        </Section>
      )}

      {/* The refund detail (doc 17 §3): the matrix row and cause lead the title, as on the row,
          then every line the ledger reverses — a refund is never one lump. */}
      {open !== null && (
        <RefundDetail row={open} onClose={() => setOpen(null)} />
      )}

      {rows.length > 0 && pages > 1 && (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}

      <Section title="Raise a refund" note="finance only · the ledger reverses it line by line">
        <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
          <Input label="Booking (BMP code)" value={code} onChange={setCode} />
          <Select
            label="Reason"
            value={reasonCode}
            options={REASON_CODES}
            onChange={setReasonCode}
          />
          {COMPONENTS.map((component) => (
            <Input
              key={component}
              label={`${component} (₹)`}
              inputMode="decimal"
              value={rupees[component] ?? ''}
              onChange={(value) => setRupees((prev) => ({ ...prev, [component]: value }))}
              style={{ width: 120 }}
            />
          ))}
          <ScopeAction
            scope={scope}
            need="finance"
            disabled={code.trim() === '' || lines.length === 0}
            onClick={() => setConfirming(true)}
          >
            Raise refund
          </ScopeAction>
        </div>
      </Section>

      <ConfirmAction
        open={confirming}
        title={`Refund ${code.trim().toUpperCase()}?`}
        phrase="REFUND"
        reason="Memo (required, kept with the refund)"
        consequences={[
          `Asks the gateway to return ${(totalPaise / 100).toFixed(2)} rupees across ${lines.length} ${lines.length === 1 ? 'line' : 'lines'}.`,
          'Money leaving cannot be called back once the gateway has it.',
        ]}
        onCancel={() => setConfirming(false)}
        onConfirm={(memo: string) => {
          setConfirming(false);
          raise.mutate(
            { booking_code: code.trim(), reason_code: reasonCode, lines, memo },
            {
              onSuccess: (row) => {
                toast({ tone: 'success', message: `Refund ${row.public_code} raised` });
                setCode('');
                setRupees({});
              },
              onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
            },
          );
        }}
      />

      {retrying !== null && (
        <ConfirmAction
          open
          title={`Send ${retrying.public_code} to the gateway again?`}
          phrase={retrying.public_code}
          reason="Why another send should succeed (kept beside the failure)"
          consequences={[
            `The gateway said: ${retrying.failure_reason ?? 'no reason was recorded'}.`,
            `Sends ${formatInr(retrying.amount_paise, { paise: true })} again under a new gateway key. Retry only once the cause is fixed — the bank replied, or the instrument is corrected.`,
          ]}
          confirmLabel="Retry"
          onCancel={() => setRetrying(null)}
          onConfirm={(reason) =>
            retry.mutate(
              { id: retrying.id, reason },
              {
                onSuccess: (row) => {
                  setRetrying(null);
                  toast({ tone: 'success', message: `${row.public_code} is queued to send again` });
                },
                onError: (e) => {
                  setRetrying(null);
                  toast({ tone: 'error', message: errorMessage(e) });
                },
              },
            )
          }
        />
      )}

      <AdjustmentDraft refunds={rows} />

      <Section
        title="Corrections awaiting approval"
        note="an adjustment is never approved by the person who raised it"
        pad={false}
      >
        {pending.isPending && <LoadingState message="Reading the queue…" />}
        {pending.isError && (
          <ErrorState title="Could not read the queue" cause={errorMessage(pending.error)} />
        )}
        {approve.isError && (
          <ErrorState
            title="That approval was refused"
            cause={errorMessage(approve.error)}
            inline
          />
        )}
        {pending.isSuccess && pending.data.items.length === 0 && (
          <EmptyState
            icon="undo-2"
            title="Nothing waiting"
            body="No correction needs a second pair of eyes."
            inline
          />
        )}
        {pending.isSuccess && pending.data.items.length > 0 && (
          <DataTable
            density="compact"
            columns={[
              { key: 'reason', label: 'Reason' },
              { key: 'direction', label: 'Direction' },
              { key: 'amount', label: 'Amount', align: 'right' },
              { key: 'memo', label: 'Why' },
              { key: 'act', label: '' },
            ]}
            rows={pending.data.items.map((row) => ({
              id: row.id,
              reason: row.reason_code,
              direction: row.direction,
              amount: <Money paise={row.amount_paise} />,
              memo: row.memo,
              act: (
                <span style={{ display: 'flex', gap: 'var(--sp-xs)' }}>
                  <ScopeAction
                    scope={scope}
                    need="finance"
                    size="sm"
                    onClick={() => approve.mutate({ id: row.id })}
                  >
                    Approve
                  </ScopeAction>
                  <ScopeAction
                    scope={scope}
                    need="finance"
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      reject.mutate({ id: row.id, reason: 'Rejected from the refunds console' })
                    }
                  >
                    Reject
                  </ScopeAction>
                </span>
              ),
            }))}
          />
        )}
      </Section>
    </>
  );
}
